<template>
  <div class="fts-listing-filter-design" v-if="isShow">
    <fts-spinner 
      :size="24" 
      class="listing-filter-design-loading" 
      v-if="loading" 
    />

    <div 
      class="listing-filter-design-message listing-filter-design-message--error" 
      v-else-if="formDataError"
    >
      <div>
        <div class="listing-filter-design-message-title">Something Went Wrong</div>
        <div class="listing-filter-design-message-description">
          {{formDataError}}
        </div>
      </div>
    </div>
    
    <template v-else>
      <div class="listing-filter-design-content">
        <div class="listing-filter-design-header">
          <div class="listing-filter-design-title">
            <div>Filter conditions</div>
          </div>
        </div>

        <div class="listing-filter-design-unavailable-message" v-if="!isBeta && isQueryScriptEnabled">
          This listing uses a Beta feature (query script). To update the filter conditions, please contact the administrator.
        </div>

        <template v-else>
          <!-- profile only -->
          <fts-form-element
            :item="{
              fieldName: 'profileOnly',
              input: 'toggleSwitch',
            }"
            :form-data="formData"
            comfort-design
          />
          <!-- filter conditions -->
          <template v-if="!formData.profileOnly">
            <div class="fts-form-group fts-form-group--comfort">
              <fts-query-designer
                :modules="modules"
                :states="states"
                :information-source="filterSelection()"
                :filter="formData.filter"
              />
              <!-- error message -->
              <div class="fts-form-error" v-if="error('filter')">
                {{ error("filter") }}
              </div>
            </div>
            <!-- query script -->
            <div class="fts-form-group fts-form-group--comfort" v-if="isBeta">
              <fts-collapsible-element
                :condition="isQueryScriptEnabled"
                label="query script"
                immersive
                beta
              >
                <fts-script-editor
                  slot="content"
                  editor-id="queryScript"
                  v-model="formData.queryScript"
                  lang="python"
                  label="Query Script"
                >
                  <template #label="{ label }">
                    {{label}} <fts-release-badge class="fts-align-top" />
                  </template>
                </fts-script-editor>
              </fts-collapsible-element>
            </div>
          </template>
        </template>
      </div>

      <div class="listing-filter-design-footer" v-if="!(!isBeta && isQueryScriptEnabled)">
        <fts-btn 
          outline 
          color="primary" 
          :loading="submitting" 
          @click="submit"
        >
          <font-awesome-icon :icon="['fas', 'check']" /> Save
        </fts-btn>
      </div>
    </template>

    <div style="position: absolute;top: 0.5rem;right: 0.5rem;">
      <fts-btn
        icon
        text
        @click="beforeClose"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </fts-btn>
    </div>

  </div>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import changesets from 'diff-json';

import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import commons from '../commons/commons';
import canvasDesign from '../commons/canvasDesign';
import queryLogic from '../commons/queryLogic';
import designValidator from '../commons/designValidator';

import FTSBtn from '../elements/FTSBtn';
import FTSCollapsibleElement from '../elements/FTSCollapsibleElement';
import FTSDesignModal from './FTSDesignModal';
import FTSFormElement from '../elements/FTSFormElement';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSQueryDesigner from './FTSQueryDesigner';
import FTSReleaseBadge from '../elements/FTSReleaseBadge';
import FTSScriptEditor from './FTSScriptEditor';
import FTSSpinner from '../elements/FTSSpinner';

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-design-modal": FTSDesignModal,
    "fts-form-element": FTSFormElement,
    "fts-label-group": FTSLabelGroup,
    "fts-query-designer": FTSQueryDesigner,
    "fts-release-badge": FTSReleaseBadge,
    "fts-script-editor": FTSScriptEditor,
    "fts-spinner": FTSSpinner
  },
  data: () => ({
    isShow: false,
    formData: null,
    originalData: null,
    resolve: null,
    loading: false,
    submitting: false,
    formDataError: null
  }),
  validations() {
    let formData = {};
    formData = designValidator.menuDesign();
    return { formData };
  },
  methods: {
    async show({ moduleId, listing }) {
      if (this.isShow) return;

      this.isShow = true;
      this.loading = true;
      this.formData = _.cloneDeep(listing);

      try {
        await canvasDesignStore.dispatch("loadVector", moduleId);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        let menu = canvasDesign.getMenu(canvas, this.formData.id);

        if (menu) {
          this.formData = _.cloneDeep(menu);

          if (!this.formData.filter) {
            this.$set(this.formData, "filter", { items: [] });
          }
        } else {
          throw "Listing does not exist";
        }

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      } catch(err) {
        console.error(err);
        this.formDataError = "The listing couldn't be found. It may have been removed or is not configured properly. Please update or configure it in Studio.";
      }

      this.loading = false;
    },
    async submit() {
      this.$v.formData.$touch();

      if (this.$v.formData.$invalid) {
        return;
      }

      const diff = changesets.diff(this.formData, this.originalData);
      if (diff && diff.length) {
        this.submitting = true;

        try {
          const formData = _.cloneDeep(this.formData);
          let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
          let menu = canvasDesign.getMenu(canvas, this.formData.id);

          if (menu) {
            menu.profileOnly = formData.profileOnly;
            menu.filter = formData.filter;
            menu.queryScript = formData.queryScript;

            canvasDesign.compactDesignBeforeSave(canvas);
            canvasDesignStore.dispatch("updateVectorCanvas", canvas);
            await canvasDesignStore.dispatch("saveVector");

            this.originalData = _.cloneDeep(this.formData);
            this.submitting = false;
            this.$toasted.global.success("Listing filters updated.");
            this.$emit("refresh");
          }
        } catch(err) {
          this.submitting = false;
          this.$toasted.global.error(err, "Failed to save listing filters.");
        }
      } else {
        this.$toasted.global.success("Listing filters updated (2)");
      }
    },
    beforeClose() {
      const diff = changesets.diff(this.formData, this.originalData);

      if (diff && diff.length) {
        this.$modal.show("dialog", {
          title: "Confirm discard changes",
          text: "Discard all changes?",
          buttons: [
            { title: "Cancel" },
            {
              title: "Discard",
              default: true,
              handler: () => {
                this.$modal.hide("dialog");
                this.close();
              },
            },
          ],
        });
      } else {
        this.close();
      }
    },
    close() {
      this.$v.$reset();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    error(key) {
      return designValidator.menuDesignError(this.$v.formData, key);
    },
    filterSelection() {
      let differences = ["date"];

      return _.differenceWith(
        queryLogic.querySelectionPrefix,
        this.userCollection ? [...differences] : [...differences, "username"],
        ({ id }, key) => id === key
      )
        .concat(
          this.information.filter(x => {
            const informationType = commons.informationTypes.find(y => y.id == x.type);
            return informationType && informationType.searchable;
          })
        )
        .concat(queryLogic.querySelectionSuffix);
    }
  },
  computed: {
    isBeta() {
      return authorizationStore.getters.isBeta || canvasDesignStore.getters.workspaceBeta;
    },
    modules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    states() {
      return canvasDesignStore.getters.canvasStates;
    },
    information() {
      return canvasDesignStore.getters.canvasInformation;
    },
    userCollection() {
      return canvasDesignStore.getters.isUserCollection;
    },
    isQueryScriptEnabled() {
      return !!(this.formData && this.formData.queryScript);
    }
  }
}
</script>

<style lang='scss' scoped>

.fts-listing-filter-design {
  position: relative;
  min-height: 125px;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border-radius: 0.25rem;
  background-color: var(--fts-card-background);
  box-shadow: var(--fts-elevation-1);
  .listing-filter-design-header {
    margin-bottom: 0.75rem;
  }
  .listing-filter-design-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }
  .listing-filter-design-title {
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5;
    color: var(--fts-text);
  }
  .listing-filter-design-unavailable-message {
    padding: 0.75rem;
    border-radius: 0.25rem;
    background-color: var(--fts-gray-100);
    color: var(--fts-text);
    font-size: 0.875rem;
    line-height: 1.5;
  }
  .listing-filter-design-loading {
    flex: 1;
  }
  .listing-filter-design-message {
    flex: 1;
    max-width: 450px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;
    padding-right: 1rem;
    color: var(fts-text);
    text-align: center;
    font-size: 0.875rem;
    line-height: 1.5;
    .listing-filter-design-message-title {
      margin-bottom: 0.375rem;
      font-weight: 500;
    }
    .listing-filter-design-message-description {
      font-size: 0.75rem;
    }
    &.listing-filter-design-message--error {
      .listing-filter-design-message-title {
        color: var(--fts-danger);
      }
    }
  }
}
</style>